import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Upload, Eye, EyeOff } from "lucide-react";
import { useMenu } from "@/menu/MenuApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/menu`;

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("menu-admin-token")}` } });

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</span>
    <input {...props} className="w-full rounded-md border border-white/10 bg-[#0a0a0b] px-3.5 py-2.5 text-sm outline-none focus:border-[#C19D60]" />
  </label>
);

const ImagePicker = ({ value, onChange, label }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth());
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Upload fallito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <span className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">{label}</span>
      <div className="flex items-center gap-3">
        {value && <img src={value} alt="" className="h-14 w-14 rounded-md object-cover" />}
        <label data-testid="image-upload-btn" className="flex cursor-pointer items-center gap-2 rounded-md border border-[#C19D60]/40 px-4 py-2.5 text-xs text-[#E5CAA4] transition-colors hover:bg-[#C19D60]/10">
          <Upload size={14} /> {busy ? "Caricamento…" : "Carica immagine"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
    </div>
  );
};

const DishModal = ({ dish, categories, allergens, onClose, onSaved }) => {
  const empty = { category_id: categories[0]?.id || "", name: { it: "", en: "" }, description: { it: "", en: "" }, price: 0, allergens: [], available: true, image: "", order: 99 };
  const [form, setForm] = useState(dish ? { ...empty, ...dish, name: { ...dish.name }, description: { ...dish.description } } : empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, order: parseInt(form.order) || 99, allergens: form.allergens.map(Number) };
      if (dish) await axios.put(`${API}/admin/dishes/${dish.id}`, payload, auth());
      else await axios.post(`${API}/admin/dishes`, payload, auth());
      toast.success("Piatto salvato");
      onSaved();
      onClose();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="dish-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-[#C19D60]/25 bg-[#101012] p-7">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-2xl font-medium">{dish ? "Modifica piatto" : "Nuovo piatto"}</h3>
          <button onClick={onClose} data-testid="dish-modal-close" aria-label="Chiudi"><X size={20} className="text-[#A3A3A3]" /></button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Categoria</span>
            <select value={form.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="dish-category-select" className="w-full rounded-md border border-white/10 bg-[#0a0a0b] px-3.5 py-2.5 text-sm outline-none focus:border-[#C19D60]">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name.it}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome (IT)" value={form.name.it} onChange={(e) => set("name", { ...form.name, it: e.target.value })} data-testid="dish-name-it" />
            <Field label="Nome (EN)" value={form.name.en} onChange={(e) => set("name", { ...form.name, en: e.target.value })} data-testid="dish-name-en" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Descrizione (IT)" value={form.description.it} onChange={(e) => set("description", { ...form.description, it: e.target.value })} data-testid="dish-desc-it" />
            <Field label="Descrizione (EN)" value={form.description.en} onChange={(e) => set("description", { ...form.description, en: e.target.value })} data-testid="dish-desc-en" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prezzo (€)" type="number" step="0.5" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="dish-price" />
            <Field label="Ordine" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="dish-order" />
          </div>
          <ImagePicker label="Foto" value={form.image} onChange={(v) => set("image", v)} />
          <div>
            <span className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Allergeni</span>
            <div className="flex flex-wrap gap-1.5" data-testid="dish-allergens">
              {allergens.map((a) => (
                <button
                  key={a.code} type="button"
                  onClick={() => set("allergens", form.allergens.includes(a.code) ? form.allergens.filter((c) => c !== a.code) : [...form.allergens, a.code])}
                  className={`rounded-full border px-2.5 py-1 text-[10px] transition-colors ${form.allergens.includes(a.code) ? "border-[#C19D60] bg-[#C19D60]/15 text-[#E5CAA4]" : "border-white/10 text-[#A3A3A3]"}`}
                >
                  {String(a.code).padStart(2, "0")} {a.it}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm text-[#c9c9c9]">
            <input type="checkbox" checked={form.available} onChange={(e) => set("available", e.target.checked)} data-testid="dish-available" className="h-4 w-4 accent-[#C19D60]" />
            Disponibile nel menu
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="dish-save-btn" className="mt-7 w-full rounded-[5px] bg-[#C19D60] py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#0a0a0b] hover:bg-[#E5CAA4] disabled:opacity-50">
          {saving ? "Salvataggio…" : "Salva piatto"}
        </button>
      </div>
    </div>
  );
};

export default function AdminPanel() {
  const { data, reload } = useMenu();
  const navigate = useNavigate();
  const [authd, setAuthd] = useState(false);
  const [tab, setTab] = useState("piatti");
  const [catFilter, setCatFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/check`, auth())
      .then(() => setAuthd(true))
      .catch(() => navigate("/menu/admin/login", { replace: true }));
  }, [navigate]);

  useEffect(() => { if (data?.settings) setSettings({ ...data.settings }); }, [data]);

  if (!authd || !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="menu-admin-loading">
        <p className="font-display animate-pulse text-xl italic text-[#C19D60]">…</p>
      </div>
    );
  }
  const dishes = data.dishes.filter((d) => !catFilter || d.category_id === catFilter);

  const toggleAvail = async (d) => {
    await axios.put(`${API}/admin/dishes/${d.id}`, { ...d, available: !d.available }, auth());
    reload();
  };
  const delDish = async (d) => {
    if (!window.confirm(`Eliminare "${d.name.it}"?`)) return;
    await axios.delete(`${API}/admin/dishes/${d.id}`, auth());
    toast.success("Piatto eliminato");
    reload();
  };
  const delCat = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name.it}" e tutti i suoi piatti?`)) return;
    await axios.delete(`${API}/admin/categories/${c.id}`, auth());
    toast.success("Categoria eliminata");
    reload();
  };
  const saveCat = async (form) => {
    const payload = { name: form.name, image: form.image, order: parseInt(form.order) || 99, available: form.available !== false };
    if (form.id) await axios.put(`${API}/admin/categories/${form.id}`, payload, auth());
    else await axios.post(`${API}/admin/categories`, payload, auth());
    toast.success("Categoria salvata");
    setEditingCat(null);
    reload();
  };
  const saveSettings = async () => {
    await axios.put(`${API}/admin/settings`, settings, auth());
    toast.success("Testi salvati");
    reload();
  };

  const inputCls = "w-full rounded-md border border-white/10 bg-[#0a0a0b] px-3.5 py-2.5 text-sm outline-none focus:border-[#C19D60]";

  return (
    <div data-testid="menu-admin-panel" className="mx-auto max-w-4xl px-5 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C19D60]">Gestione menu</p>
          <h1 className="font-display mt-1 text-3xl font-semibold">Pannello amministratore</h1>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/menu" data-testid="menu-admin-back" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors hover:text-[#E5CAA4]">
            ← Torna al menu
          </Link>
          <button
            data-testid="menu-admin-logout"
            onClick={() => { localStorage.removeItem("menu-admin-token"); navigate("/menu/admin/login"); }}
            className="flex items-center gap-2 rounded-md border border-white/10 px-4 py-2 text-xs text-[#A3A3A3] transition-colors hover:border-red-400/40 hover:text-red-300"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
      </div>

      <div className="mt-8 flex gap-1 border-b border-white/10" data-testid="menu-admin-tabs">
        {[["piatti", "Piatti"], ["categorie", "Categorie"], ["testi", "Testi & Impostazioni"]].map(([k, label]) => (
          <button key={k} data-testid={`menu-admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-3 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors ${tab === k ? "border-b-2 border-[#C19D60] text-[#E5CAA4]" : "text-[#A3A3A3] hover:text-[#F8F5F0]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "piatti" && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="admin-cat-filter" className={`${inputCls} max-w-xs`}>
              <option value="">Tutte le categorie</option>
              {data.categories.map((c) => <option key={c.id} value={c.id}>{c.name.it}</option>)}
            </select>
            <button onClick={() => setEditing({})} data-testid="admin-new-dish" className="flex items-center gap-2 rounded-[5px] bg-[#C19D60] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0a0a0b] hover:bg-[#E5CAA4]">
              <Plus size={14} /> Nuovo piatto
            </button>
          </div>
          <div className="mt-6 space-y-2" data-testid="admin-dish-list">
            {dishes.map((d) => (
              <div key={d.id} data-testid={`admin-dish-${d.id}`} className="flex items-center gap-4 rounded-lg border border-white/5 bg-[#101012] p-3">
                {d.image && <img src={d.image} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{d.name.it} <span className="text-[#666]">/ {d.name.en}</span></p>
                  <p className="text-xs text-[#A3A3A3]">{data.categories.find((c) => c.id === d.category_id)?.name.it} · {d.price}€</p>
                </div>
                <button onClick={() => toggleAvail(d)} data-testid={`admin-toggle-${d.id}`} title="Disponibilità" className={`p-2 ${d.available ? "text-[#C19D60]" : "text-[#555]"}`}>
                  {d.available ? <Eye size={17} /> : <EyeOff size={17} />}
                </button>
                <button onClick={() => setEditing(d)} data-testid={`admin-edit-${d.id}`} className="p-2 text-[#A3A3A3] hover:text-[#E5CAA4]"><Pencil size={16} /></button>
                <button onClick={() => delDish(d)} data-testid={`admin-delete-${d.id}`} className="p-2 text-[#A3A3A3] hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "categorie" && (
        <div className="mt-8">
          <button onClick={() => setEditingCat({ name: { it: "", en: "" }, image: "", order: data.categories.length + 1 })} data-testid="admin-new-category" className="flex items-center gap-2 rounded-[5px] bg-[#C19D60] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0a0a0b] hover:bg-[#E5CAA4]">
            <Plus size={14} /> Nuova categoria
          </button>
          <div className="mt-6 space-y-2" data-testid="admin-category-list">
            {data.categories.map((c) => (
              <div key={c.id} data-testid={`admin-category-${c.id}`} className="flex items-center gap-4 rounded-lg border border-white/5 bg-[#101012] p-3">
                {c.image && <img src={c.image} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${c.available === false ? "opacity-50 line-through" : ""}`}>{c.name.it} <span className="text-[#666]">/ {c.name.en}</span></p>
                  <p className="text-xs text-[#A3A3A3]">Ordine: {c.order} · {data.dishes.filter((d) => d.category_id === c.id).length} piatti{c.available === false ? " · nascosta" : ""}</p>
                </div>
                <button
                  onClick={async () => { await axios.put(`${API}/admin/categories/${c.id}`, { name: c.name, image: c.image, order: c.order, available: c.available === false ? true : false }, auth()); reload(); }}
                  data-testid={`admin-toggle-cat-${c.id}`} title="Visibilità" className={`p-2 ${c.available !== false ? "text-[#C19D60]" : "text-[#555]"}`}
                >
                  {c.available !== false ? <Eye size={17} /> : <EyeOff size={17} />}
                </button>
                <button onClick={() => setEditingCat({ ...c, name: { ...c.name } })} data-testid={`admin-edit-cat-${c.id}`} className="p-2 text-[#A3A3A3] hover:text-[#E5CAA4]"><Pencil size={16} /></button>
                <button onClick={() => delCat(c)} data-testid={`admin-delete-cat-${c.id}`} className="p-2 text-[#A3A3A3] hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          {editingCat && (
            <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" data-testid="category-modal">
              <div className="w-full max-w-md rounded-lg border border-[#C19D60]/25 bg-[#101012] p-7">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="font-display text-2xl font-medium">{editingCat.id ? "Modifica categoria" : "Nuova categoria"}</h3>
                  <button onClick={() => setEditingCat(null)} aria-label="Chiudi"><X size={20} className="text-[#A3A3A3]" /></button>
                </div>
                <div className="space-y-4">
                  <Field label="Nome (IT)" value={editingCat.name.it} onChange={(e) => setEditingCat({ ...editingCat, name: { ...editingCat.name, it: e.target.value } })} data-testid="cat-name-it" />
                  <Field label="Nome (EN)" value={editingCat.name.en} onChange={(e) => setEditingCat({ ...editingCat, name: { ...editingCat.name, en: e.target.value } })} data-testid="cat-name-en" />
                  <Field label="Ordine" type="number" value={editingCat.order} onChange={(e) => setEditingCat({ ...editingCat, order: e.target.value })} data-testid="cat-order" />
                  <ImagePicker label="Foto di copertina" value={editingCat.image} onChange={(v) => setEditingCat({ ...editingCat, image: v })} />
                </div>
                <button onClick={() => saveCat(editingCat)} data-testid="cat-save-btn" className="mt-7 w-full rounded-[5px] bg-[#C19D60] py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#0a0a0b] hover:bg-[#E5CAA4]">
                  Salva categoria
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "testi" && settings && (
        <div className="mt-8 space-y-5" data-testid="admin-settings">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome ristorante" value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} data-testid="set-name" />
            <Field label="Instagram" value={settings.instagram} onChange={(e) => setSettings({ ...settings, instagram: e.target.value })} data-testid="set-instagram" />
            <Field label="Claim (IT)" value={settings.claim_it} onChange={(e) => setSettings({ ...settings, claim_it: e.target.value })} data-testid="set-claim-it" />
            <Field label="Claim (EN)" value={settings.claim_en} onChange={(e) => setSettings({ ...settings, claim_en: e.target.value })} data-testid="set-claim-en" />
            <Field label="Sottotitolo hero (IT)" value={settings.hero_sub_it} onChange={(e) => setSettings({ ...settings, hero_sub_it: e.target.value })} data-testid="set-hero-it" />
            <Field label="Sottotitolo hero (EN)" value={settings.hero_sub_en} onChange={(e) => setSettings({ ...settings, hero_sub_en: e.target.value })} data-testid="set-hero-en" />
            <Field label="Avviso marquee (IT)" value={settings.marquee_notice_it} onChange={(e) => setSettings({ ...settings, marquee_notice_it: e.target.value })} data-testid="set-marquee-it" />
            <Field label="Avviso marquee (EN)" value={settings.marquee_notice_en} onChange={(e) => setSettings({ ...settings, marquee_notice_en: e.target.value })} data-testid="set-marquee-en" />
            <Field label="Indirizzo" value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} data-testid="set-address" />
            <Field label="Telefono" value={settings.phone} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} data-testid="set-phone" />
            <Field label="Badge (IT)" value={settings.badge_it} onChange={(e) => setSettings({ ...settings, badge_it: e.target.value })} data-testid="set-badge-it" />
            <Field label="Badge (EN)" value={settings.badge_en} onChange={(e) => setSettings({ ...settings, badge_en: e.target.value })} data-testid="set-badge-en" />
          </div>
          <div>
            <p className="mb-3 text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">Filosofia (3 colonne)</p>
            <div className="space-y-4">
              {settings.philosophy.map((p, i) => (
                <div key={i} className="grid gap-3 rounded-lg border border-white/5 bg-[#101012] p-4 sm:grid-cols-2">
                  <Field label={`Titolo ${i + 1} (IT)`} value={p.it_t} onChange={(e) => { const ph = [...settings.philosophy]; ph[i] = { ...ph[i], it_t: e.target.value }; setSettings({ ...settings, philosophy: ph }); }} />
                  <Field label={`Titolo ${i + 1} (EN)`} value={p.en_t} onChange={(e) => { const ph = [...settings.philosophy]; ph[i] = { ...ph[i], en_t: e.target.value }; setSettings({ ...settings, philosophy: ph }); }} />
                  <Field label={`Testo ${i + 1} (IT)`} value={p.it_d} onChange={(e) => { const ph = [...settings.philosophy]; ph[i] = { ...ph[i], it_d: e.target.value }; setSettings({ ...settings, philosophy: ph }); }} />
                  <Field label={`Testo ${i + 1} (EN)`} value={p.en_d} onChange={(e) => { const ph = [...settings.philosophy]; ph[i] = { ...ph[i], en_d: e.target.value }; setSettings({ ...settings, philosophy: ph }); }} />
                </div>
              ))}
            </div>
          </div>
          <button onClick={saveSettings} data-testid="settings-save-btn" className="rounded-[5px] bg-[#C19D60] px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#0a0a0b] hover:bg-[#E5CAA4]">
            Salva testi
          </button>
        </div>
      )}

      {editing !== null && (
        <DishModal dish={editing.id ? editing : null} categories={data.categories} allergens={data.allergens} onClose={() => setEditing(null)} onSaved={reload} />
      )}
    </div>
  );
}
